import { QueryCache, QueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

import { APP_VERSION } from "@shared/version";
import { ApiError } from "../api/client";

// ---------------------------------------------------------------------------
// QueryClient — zentrale Konfiguration fuers ganze Frontend
// ---------------------------------------------------------------------------
// Ein Provider, ein Cache. Cache-Keys und Hooks leben in /hooks, Utilities in /lib/query/keys.

// Persist-Cache-Buster (Session 16): haengt an der App-Version.
// Bei Version-Bumps werden alte localStorage-Caches automatisch verworfen.
export const QUERY_CACHE_VERSION = `v${APP_VERSION}`;

// Der localStorage-Schluessel des gespiegelten Caches. Steht hier, weil ihn
// zwei Stellen kennen muessen: der QueryProvider schreibt ihn, und der
// Identitaets-Waechter (cacheIdentity.ts) wirft ihn beim Wechsel der Person
// weg. Zwei Literale waeren zwei Wahrheiten — eine davon irgendwann falsch.
export const QUERY_CACHE_STORAGE_KEY = "bergler.query-cache";

/**
 * Was in den localStorage-Spiegel darf: nur erfolgreiche Abfragen, und keine,
 * die sich mit `meta: { spiegel: false }` ausnimmt (FRAGEN-1 — vertraulicher
 * Inhalt bleibt im Arbeitsspeicher des Tabs).
 */
export function sollGespiegeltWerden(
  status: string,
  meta: Record<string, unknown> | undefined,
): boolean {
  return status === "success" && meta?.spiegel !== false;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // 1 Minute Fresh — Ranked-Listen aktualisieren alle paar Navigationen.
        staleTime: 60_000,
        // 24 h im Cache (Session 16: > Persist-maxAge, sonst kollidiert GC mit
        // dem persistierten Snapshot und der Hydrate-Pfad sieht "leeren" Cache).
        gcTime: 24 * 60 * 60_000,
        // Auth/Validation-Fehler nicht retryen — Netzwerk-Fehler einmal.
        retry: (failureCount, error) => {
          if (error instanceof ApiError) {
            if (error.status === 401 || error.status === 403) return false;
            if (error.status === 404) return false;
          }
          return failureCount < 1;
        },
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Auth-Fehler sind erwartet (Logout/Ablauf) — nicht nach Sentry.
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          return;
        }
        Sentry.captureException(error, {
          tags: { queryKey: JSON.stringify(query.queryKey) },
        });
      },
    }),
  });
}
